/**
 * angles.ts — small, pure math helpers. No three.js and no DOM, so every
 * function here is trivially unit-testable.
 */

export const TAU = Math.PI * 2;
export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

/** Clamp `v` into the inclusive range [lo, hi]. */
export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Linear interpolation from a to b by t (t usually in [0,1]; not clamped). */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Where does `v` sit between a and b, as a 0..1 fraction? Inverse of lerp. */
export function inverseLerp(a: number, b: number, v: number): number {
  return a === b ? 0 : (v - a) / (b - a);
}

/**
 * Frame-rate-independent smoothing ("exponential damp"). Eases `current`
 * toward `target`; `lambda` is responsiveness (higher = snappier). Because it
 * factors in `dt`, the feel is the same regardless of frame rate.
 */
export function damp(current: number, target: number, lambda: number, dt: number): number {
  return lerp(current, target, 1 - Math.exp(-lambda * dt));
}

/** Move `current` toward `target` by at most `maxDelta` (linear, no overshoot). */
export function moveTowards(current: number, target: number, maxDelta: number): number {
  const diff = target - current;
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
}

/** Normalise an angle into the range (-PI, PI]. */
export function wrapAngle(a: number): number {
  a = a % TAU;
  if (a <= -Math.PI) a += TAU;
  else if (a > Math.PI) a -= TAU;
  return a;
}

/**
 * Shortest signed angular distance from `from` to `to`, in (-PI, PI].
 * Used so smoothed rotations take the short way round instead of unwinding the
 * long way (e.g. from 170° to -170° is +20°, not -340°).
 */
export function shortestAngleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}
